'use client';

import React, { useState } from 'react';
import { Search, Eye, Copy, Pencil, X, Check, Zap, Gift, RefreshCw, Mail, ChevronRight, Star } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Badge from '@/components/ui/Badge';

// ─── Types ────────────────────────────────────────────────────────────────────

type TemplateCategory = 'all' | 'welcome' | 'promotional' | 're-engagement';

interface EmailTemplate {
  id: string;
  name: string;
  category: Exclude<TemplateCategory, 'all'>;
  description: string;
  subject: string;
  previewText: string;
  bodyHtml: string;
  tags: string[];
  usageCount: number;
  featured?: boolean;
}

// ─── Template Data ─────────────────────────────────────────────────────────────

const TEMPLATES: EmailTemplate[] = [
  {
    id: 'welcome-1',
    name: 'Warm Welcome',
    category: 'welcome',
    description: 'Greet new subscribers with a friendly intro and a clear next step.',
    subject: 'Welcome to {{company_name}} — you\'re in! 🎉',
    previewText: 'We\'re thrilled to have you on board.',
    tags: ['onboarding', 'new user'],
    usageCount: 1240,
    featured: true,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="background:linear-gradient(135deg,#5b35e8 0%,#8b5cf6 60%,#ff5c2b 100%);padding:48px 40px;text-align:center;border-radius:12px 12px 0 0;">
    <h1 style="color:#ffffff;font-size:28px;font-weight:800;margin:0 0 8px;">Welcome aboard, {{first_name}}! 🎉</h1>
    <p style="color:rgba(255,255,255,0.85);font-size:16px;margin:0;">You've just joined {{company_name}}.</p>
  </div>
  <div style="padding:40px;">
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 24px;">Hi {{first_name}},</p>
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 24px;">We're so glad you're here. Your account is ready and waiting — here's what you can do right now:</p>
    <div style="background:#f9f7ff;border-radius:10px;padding:24px;margin:0 0 28px;">
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
        <span style="background:#5b35e8;color:#fff;border-radius:50%;width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;">1</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>Complete your profile</strong> — add a photo and bio so your team knows you.</p>
      </div>
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
        <span style="background:#5b35e8;color:#fff;border-radius:50%;width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;">2</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>Explore the dashboard</strong> — get a feel for everything at your fingertips.</p>
      </div>
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <span style="background:#5b35e8;color:#fff;border-radius:50%;width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;">3</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>Invite your team</strong> — collaboration is better together.</p>
      </div>
    </div>
    <div style="text-align:center;margin:0 0 32px;">
      <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:16px;font-weight:700;padding:14px 36px;border-radius:8px;text-decoration:none;">Get Started →</a>
    </div>
    <p style="color:#6b7280;font-size:14px;line-height:1.6;margin:0;">Questions? Just reply to this email — we're always happy to help.</p>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'welcome-2',
    name: 'Quick-Start Guide',
    category: 'welcome',
    description: 'A step-by-step onboarding email that gets users to their first win fast.',
    subject: 'Your quick-start guide is here, {{first_name}} 🚀',
    previewText: 'Three steps to get the most out of {{company_name}}.',
    tags: ['onboarding', 'tutorial'],
    usageCount: 870,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="padding:40px 40px 0;text-align:center;">
    <div style="display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;background:linear-gradient(135deg,#5b35e8,#8b5cf6);border-radius:14px;margin-bottom:20px;">
      <span style="font-size:24px;">🚀</span>
    </div>
    <h1 style="color:#111827;font-size:26px;font-weight:800;margin:0 0 8px;">Your quick-start guide</h1>
    <p style="color:#6b7280;font-size:16px;margin:0 0 32px;">Follow these 3 steps to hit the ground running.</p>
  </div>
  <div style="padding:0 40px 40px;">
    <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-bottom:12px;">
      <div style="padding:20px;display:flex;align-items:center;gap:16px;">
        <span style="font-size:28px;">👤</span>
        <div>
          <p style="margin:0 0 4px;font-weight:700;color:#111827;font-size:15px;">Step 1 — Set up your profile</p>
          <p style="margin:0;color:#6b7280;font-size:14px;">Takes 2 minutes. Helps your team recognise you.</p>
        </div>
        <a href="{{step1_url}}" style="margin-left:auto;color:#5b35e8;font-size:13px;font-weight:600;text-decoration:none;white-space:nowrap;">Do it →</a>
      </div>
    </div>
    <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-bottom:12px;">
      <div style="padding:20px;display:flex;align-items:center;gap:16px;">
        <span style="font-size:28px;">📊</span>
        <div>
          <p style="margin:0 0 4px;font-weight:700;color:#111827;font-size:15px;">Step 2 — Explore your dashboard</p>
          <p style="margin:0;color:#6b7280;font-size:14px;">See your data, reports, and key metrics.</p>
        </div>
        <a href="{{step2_url}}" style="margin-left:auto;color:#5b35e8;font-size:13px;font-weight:600;text-decoration:none;white-space:nowrap;">Explore →</a>
      </div>
    </div>
    <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-bottom:32px;">
      <div style="padding:20px;display:flex;align-items:center;gap:16px;">
        <span style="font-size:28px;">🤝</span>
        <div>
          <p style="margin:0 0 4px;font-weight:700;color:#111827;font-size:15px;">Step 3 — Invite your team</p>
          <p style="margin:0;color:#6b7280;font-size:14px;">Better together — bring your colleagues along.</p>
        </div>
        <a href="{{step3_url}}" style="margin-left:auto;color:#5b35e8;font-size:13px;font-weight:600;text-decoration:none;white-space:nowrap;">Invite →</a>
      </div>
    </div>
    <div style="text-align:center;">
      <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:16px;font-weight:700;padding:14px 36px;border-radius:8px;text-decoration:none;">Go to my dashboard</a>
    </div>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'promo-1',
    name: 'Flash Sale',
    category: 'promotional',
    description: 'Drive urgency with a time-limited discount offer and bold CTA.',
    subject: '⚡ {{discount}}% off — today only, {{first_name}}',
    previewText: 'This deal expires at midnight. Don\'t miss it.',
    tags: ['sale', 'discount', 'urgency'],
    usageCount: 2100,
    featured: true,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="background:#111827;padding:48px 40px;text-align:center;border-radius:12px 12px 0 0;position:relative;overflow:hidden;">
    <div style="position:absolute;top:-30px;right:-30px;width:120px;height:120px;background:radial-gradient(circle,rgba(255,92,43,0.4),transparent);border-radius:50%;"></div>
    <div style="position:absolute;bottom:-20px;left:-20px;width:80px;height:80px;background:radial-gradient(circle,rgba(91,53,232,0.4),transparent);border-radius:50%;"></div>
    <p style="color:#ff5c2b;font-size:13px;font-weight:700;letter-spacing:0.15em;text-transform:uppercase;margin:0 0 12px;">Limited Time Offer</p>
    <h1 style="color:#ffffff;font-size:56px;font-weight:900;margin:0 0 4px;line-height:1;">{{discount}}%</h1>
    <p style="color:#ffffff;font-size:22px;font-weight:700;margin:0 0 16px;">OFF EVERYTHING</p>
    <p style="color:rgba(255,255,255,0.6);font-size:14px;margin:0;">Use code <strong style="color:#ff5c2b;background:rgba(255,92,43,0.15);padding:2px 8px;border-radius:4px;">{{promo_code}}</strong> at checkout</p>
  </div>
  <div style="padding:40px;text-align:center;">
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 8px;">Hey {{first_name}},</p>
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 28px;">We're running a flash sale — <strong>{{discount}}% off</strong> everything in the store. But it ends at midnight tonight, so don't wait.</p>
    <div style="background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:20px;margin:0 0 28px;display:inline-block;width:100%;box-sizing:border-box;">
      <p style="margin:0 0 4px;color:#92400e;font-size:13px;font-weight:700;text-transform:uppercase;letter-spacing:0.1em;">⏰ Offer expires in</p>
      <p style="margin:0;color:#c2410c;font-size:28px;font-weight:900;">{{hours_remaining}}h {{minutes_remaining}}m</p>
    </div>
    <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#ff5c2b,#ef4444);color:#ffffff;font-size:17px;font-weight:800;padding:16px 44px;border-radius:8px;text-decoration:none;margin-bottom:16px;">Shop Now — {{discount}}% Off ⚡</a>
    <p style="color:#9ca3af;font-size:13px;margin:0;">No code needed — discount applied automatically.</p>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'promo-2',
    name: 'New Product Launch',
    category: 'promotional',
    description: 'Announce a new product or feature with excitement and a clear CTA.',
    subject: '🎁 Introducing {{product_name}} — built for you',
    previewText: 'Something new just dropped. You\'re going to love it.',
    tags: ['launch', 'product', 'announcement'],
    usageCount: 1560,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="padding:48px 40px 32px;text-align:center;">
    <span style="display:inline-block;background:linear-gradient(135deg,rgba(91,53,232,0.1),rgba(255,92,43,0.1));color:#5b35e8;font-size:12px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;padding:6px 16px;border-radius:100px;margin-bottom:20px;">New Release</span>
    <h1 style="color:#111827;font-size:30px;font-weight:800;margin:0 0 12px;line-height:1.2;">Introducing<br/><span style="background:linear-gradient(135deg,#5b35e8,#ff5c2b);-webkit-background-clip:text;-webkit-text-fill-color:transparent;">{{product_name}}</span></h1>
    <p style="color:#6b7280;font-size:16px;line-height:1.7;margin:0 0 32px;">{{product_tagline}}</p>
    <div style="background:linear-gradient(135deg,#f9f7ff,#fff5f0);border-radius:16px;padding:32px;margin:0 0 32px;">
      <p style="color:#374151;font-size:15px;line-height:1.8;margin:0 0 20px;">{{product_description}}</p>
      <div style="display:flex;gap:12px;flex-wrap:wrap;justify-content:center;">
        <span style="background:#ffffff;border:1px solid #e5e7eb;border-radius:8px;padding:8px 16px;font-size:14px;color:#374151;">✓ {{feature_1}}</span>
        <span style="background:#ffffff;border:1px solid #e5e7eb;border-radius:8px;padding:8px 16px;font-size:14px;color:#374151;">✓ {{feature_2}}</span>
        <span style="background:#ffffff;border:1px solid #e5e7eb;border-radius:8px;padding:8px 16px;font-size:14px;color:#374151;">✓ {{feature_3}}</span>
      </div>
    </div>
    <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:16px;font-weight:700;padding:14px 40px;border-radius:8px;text-decoration:none;">See {{product_name}} →</a>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'promo-3',
    name: 'Seasonal Campaign',
    category: 'promotional',
    description: 'Tie your offer to a season or holiday for higher relevance and open rates.',
    subject: '🌟 {{season}} deals just for you, {{first_name}}',
    previewText: 'Celebrate the season with exclusive savings.',
    tags: ['seasonal', 'holiday', 'offer'],
    usageCount: 980,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="background:linear-gradient(135deg,#5b35e8 0%,#8b5cf6 50%,#ff5c2b 100%);padding:48px 40px;text-align:center;border-radius:12px 12px 0 0;">
    <p style="font-size:48px;margin:0 0 12px;">{{season_emoji}}</p>
    <h1 style="color:#ffffff;font-size:28px;font-weight:800;margin:0 0 8px;">{{season}} Deals Are Here</h1>
    <p style="color:rgba(255,255,255,0.8);font-size:16px;margin:0;">Exclusive savings for our valued subscribers.</p>
  </div>
  <div style="padding:40px;">
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 24px;">Hi {{first_name}},</p>
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 28px;">{{season}} is the perfect time to treat yourself (or someone special). We've put together our best deals of the year just for you.</p>
    <div style="display:grid;gap:12px;margin:0 0 32px;">
      <div style="border:1px solid #e5e7eb;border-radius:10px;padding:20px;display:flex;align-items:center;justify-content:space-between;">
        <div>
          <p style="margin:0 0 4px;font-weight:700;color:#111827;font-size:15px;">{{deal_1_name}}</p>
          <p style="margin:0;color:#6b7280;font-size:14px;">{{deal_1_description}}</p>
        </div>
        <span style="background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#fff;font-size:14px;font-weight:700;padding:6px 14px;border-radius:6px;white-space:nowrap;">{{deal_1_discount}}</span>
      </div>
      <div style="border:1px solid #e5e7eb;border-radius:10px;padding:20px;display:flex;align-items:center;justify-content:space-between;">
        <div>
          <p style="margin:0 0 4px;font-weight:700;color:#111827;font-size:15px;">{{deal_2_name}}</p>
          <p style="margin:0;color:#6b7280;font-size:14px;">{{deal_2_description}}</p>
        </div>
        <span style="background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#fff;font-size:14px;font-weight:700;padding:6px 14px;border-radius:6px;white-space:nowrap;">{{deal_2_discount}}</span>
      </div>
    </div>
    <div style="text-align:center;">
      <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:16px;font-weight:700;padding:14px 40px;border-radius:8px;text-decoration:none;">Shop {{season}} Deals</a>
    </div>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'reeng-1',
    name: 'We Miss You',
    category: 're-engagement',
    description: 'Win back inactive subscribers with a personal, empathetic message.',
    subject: 'We miss you, {{first_name}} 👋',
    previewText: 'It\'s been a while — here\'s what you\'ve been missing.',
    tags: ['win-back', 'inactive', 'personal'],
    usageCount: 1890,
    featured: true,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="padding:48px 40px 0;text-align:center;">
    <p style="font-size:56px;margin:0 0 16px;">👋</p>
    <h1 style="color:#111827;font-size:28px;font-weight:800;margin:0 0 8px;">We miss you, {{first_name}}</h1>
    <p style="color:#6b7280;font-size:16px;margin:0 0 32px;">It's been {{days_inactive}} days since your last visit.</p>
  </div>
  <div style="padding:0 40px 40px;">
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 24px;">We've been busy building new things and we'd love to show you what's changed. Here's a quick peek at what you've missed:</p>
    <div style="background:#f9f7ff;border-radius:12px;padding:24px;margin:0 0 28px;">
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
        <span style="font-size:20px;">✨</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>{{update_1_title}}</strong> — {{update_1_description}}</p>
      </div>
      <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:16px;">
        <span style="font-size:20px;">🚀</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>{{update_2_title}}</strong> — {{update_2_description}}</p>
      </div>
      <div style="display:flex;align-items:flex-start;gap:12px;">
        <span style="font-size:20px;">🎯</span>
        <p style="margin:0;color:#374151;font-size:15px;"><strong>{{update_3_title}}</strong> — {{update_3_description}}</p>
      </div>
    </div>
    <div style="background:linear-gradient(135deg,rgba(91,53,232,0.06),rgba(255,92,43,0.04));border-radius:10px;padding:24px;margin:0 0 28px;text-align:center;">
      <p style="color:#374151;font-size:15px;margin:0 0 4px;">As a welcome-back gift:</p>
      <p style="color:#5b35e8;font-size:22px;font-weight:800;margin:0 0 4px;">{{discount}}% off your next order</p>
      <p style="color:#6b7280;font-size:13px;margin:0;">Use code <strong>{{promo_code}}</strong> — expires in 7 days</p>
    </div>
    <div style="text-align:center;">
      <a href="{{cta_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:16px;font-weight:700;padding:14px 40px;border-radius:8px;text-decoration:none;">Come back and explore →</a>
    </div>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'reeng-2',
    name: 'Last Chance',
    category: 're-engagement',
    description: 'Final attempt to re-engage before removing from the list.',
    subject: 'Should we say goodbye, {{first_name}}?',
    previewText: 'We\'re about to remove you — unless you want to stay.',
    tags: ['win-back', 'churn', 'final'],
    usageCount: 740,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="padding:48px 40px;text-align:center;">
    <p style="font-size:48px;margin:0 0 16px;">😢</p>
    <h1 style="color:#111827;font-size:26px;font-weight:800;margin:0 0 12px;">Is this goodbye, {{first_name}}?</h1>
    <p style="color:#6b7280;font-size:16px;line-height:1.7;margin:0 0 32px;">You haven't opened our emails in a while. We don't want to clutter your inbox, so we're giving you a choice.</p>
    <div style="display:flex;gap:12px;justify-content:center;margin:0 0 32px;flex-wrap:wrap;">
      <a href="{{stay_url}}" style="display:inline-block;background:linear-gradient(135deg,#5b35e8,#ff5c2b);color:#ffffff;font-size:15px;font-weight:700;padding:13px 28px;border-radius:8px;text-decoration:none;">✓ Keep me subscribed</a>
      <a href="{{unsubscribe_url}}" style="display:inline-block;background:#f3f4f6;color:#6b7280;font-size:15px;font-weight:600;padding:13px 28px;border-radius:8px;text-decoration:none;">Unsubscribe me</a>
    </div>
    <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:10px;padding:16px 20px;">
      <p style="color:#92400e;font-size:14px;margin:0;">⏰ If you don't click either button, we'll remove you automatically in <strong>7 days</strong>.</p>
    </div>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
  {
    id: 'reeng-3',
    name: 'Survey & Feedback',
    category: 're-engagement',
    description: 'Re-engage dormant users by asking for feedback — shows you care.',
    subject: 'Quick question for you, {{first_name}} 🙏',
    previewText: 'We want to hear from you — takes 30 seconds.',
    tags: ['survey', 'feedback', 'win-back'],
    usageCount: 620,
    bodyHtml: `<div style="font-family:DM Sans,sans-serif;max-width:600px;margin:0 auto;background:#ffffff;">
  <div style="padding:48px 40px 0;text-align:center;">
    <p style="font-size:48px;margin:0 0 16px;">🙏</p>
    <h1 style="color:#111827;font-size:26px;font-weight:800;margin:0 0 8px;">We'd love your feedback</h1>
    <p style="color:#6b7280;font-size:16px;margin:0 0 32px;">It takes 30 seconds and helps us improve for you.</p>
  </div>
  <div style="padding:0 40px 40px;">
    <p style="color:#374151;font-size:16px;line-height:1.7;margin:0 0 24px;">Hi {{first_name}}, we noticed you haven't been around lately. We'd love to know why — your honest feedback helps us build a better product.</p>
    <p style="color:#374151;font-size:15px;font-weight:600;margin:0 0 12px;">Why haven't you been using {{company_name}}?</p>
    <div style="display:flex;flex-direction:column;gap:8px;margin:0 0 28px;">
      <a href="{{reason_1_url}}" style="display:block;border:1px solid #e5e7eb;border-radius:8px;padding:14px 20px;color:#374151;text-decoration:none;font-size:15px;transition:all 0.15s;">😕 It doesn't meet my needs</a>
      <a href="{{reason_2_url}}" style="display:block;border:1px solid #e5e7eb;border-radius:8px;padding:14px 20px;color:#374151;text-decoration:none;font-size:15px;">💸 It's too expensive</a>
      <a href="{{reason_3_url}}" style="display:block;border:1px solid #e5e7eb;border-radius:8px;padding:14px 20px;color:#374151;text-decoration:none;font-size:15px;">🤷 I just forgot about it</a>
      <a href="{{reason_4_url}}" style="display:block;border:1px solid #e5e7eb;border-radius:8px;padding:14px 20px;color:#374151;text-decoration:none;font-size:15px;">📬 Too many emails</a>
    </div>
    <p style="color:#6b7280;font-size:14px;text-align:center;margin:0;">Your response is anonymous and takes 30 seconds.</p>
  </div>
  <div style="background:#f3f4f6;padding:20px 40px;text-align:center;border-radius:0 0 12px 12px;">
    <p style="color:#9ca3af;font-size:12px;margin:0;">© {{year}} {{company_name}} · <a href="{{unsubscribe_url}}" style="color:#9ca3af;">Unsubscribe</a></p>
  </div>
</div>`,
  },
];

// ─── Category Config ───────────────────────────────────────────────────────────

const CATEGORIES: { value: TemplateCategory; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'all', label: 'All Templates', icon: Mail, color: '#6b7280' },
  { value: 'welcome', label: 'Welcome', icon: Zap, color: '#5b35e8' },
  { value: 'promotional', label: 'Promotional', icon: Gift, color: '#ff5c2b' },
  { value: 're-engagement', label: 'Re-engagement', icon: RefreshCw, color: '#0ea5e9' },
];

const categoryBadgeVariant: Record<Exclude<TemplateCategory, 'all'>, 'primary' | 'warning' | 'info'> = {
  welcome: 'primary',
  promotional: 'warning',
  're-engagement': 'info',
};

// ─── Customize Modal ───────────────────────────────────────────────────────────

interface CustomizeModalProps {
  template: EmailTemplate;
  onClose: () => void;
  onUse: (template: EmailTemplate, subject: string, body: string) => void;
}

function CustomizeModal({ template, onClose, onUse }: CustomizeModalProps) {
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.bodyHtml);
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(body).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Modal isOpen onClose={onClose} title={`Customize — ${template.name}`} size="xl">
      <div className="flex flex-col gap-4">
        {/* Subject line */}
        <div>
          <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">Subject Line</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            placeholder="Email subject..."
          />
        </div>

        {/* Tab switcher */}
        <div className="flex gap-1 p-1 bg-secondary rounded-lg w-fit">
          <button
            onClick={() => setTab('edit')}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${tab === 'edit' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Edit HTML
          </button>
          <button
            onClick={() => setTab('preview')}
            className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${tab === 'preview' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Preview
          </button>
        </div>

        {/* Content area */}
        {tab === 'edit' ? (
          <div className="relative">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={16}
              className="w-full px-3 py-3 rounded-lg border border-border bg-background text-xs font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
              spellCheck={false}
            />
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-secondary text-xs font-semibold text-muted-foreground hover:text-foreground transition-all"
            >
              {copied ? <Check size={12} className="text-success" /> : <Copy size={12} />}
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ) : (
          <div className="border border-border rounded-lg overflow-hidden" style={{ height: '400px' }}>
            <iframe
              srcDoc={body}
              title="Email preview"
              className="w-full h-full"
              sandbox="allow-same-origin"
            />
          </div>
        )}

        {/* Variables hint */}
        <div className="bg-secondary/60 rounded-lg px-3 py-2.5">
          <p className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">Merge tags:</span> Replace{' '}
            <code className="bg-card px-1 py-0.5 rounded text-xs">{'{{variable}}'}</code> placeholders with your actual values before sending.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-1">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            Cancel
          </button>
          <button
            onClick={() => { onUse(template, subject, body); onClose(); }}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all"
            style={{ background: 'linear-gradient(135deg,#5b35e8,#ff5c2b)' }}
          >
            <ChevronRight size={15} />
            Use This Template
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Preview Modal ─────────────────────────────────────────────────────────────

interface PreviewModalProps {
  template: EmailTemplate;
  onClose: () => void;
  onCustomize: () => void;
}

function PreviewModal({ template, onClose, onCustomize }: PreviewModalProps) {
  return (
    <Modal isOpen onClose={onClose} title={template.name} size="xl">
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={categoryBadgeVariant[template.category]} size="sm">
              {template.category}
            </Badge>
            {template.tags.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full bg-secondary text-xs text-muted-foreground font-medium">{t}</span>
            ))}
          </div>
          <span className="text-xs text-muted-foreground">{template.usageCount.toLocaleString()} uses</span>
        </div>

        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Subject</p>
          <p className="text-sm font-medium text-foreground bg-secondary/50 px-3 py-2 rounded-lg">{template.subject}</p>
        </div>

        <div className="border border-border rounded-lg overflow-hidden" style={{ height: '440px' }}>
          <iframe
            srcDoc={template.bodyHtml}
            title={`Preview of ${template.name}`}
            className="w-full h-full"
            sandbox="allow-same-origin"
          />
        </div>

        <div className="flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary transition-all">
            Close
          </button>
          <button
            onClick={onCustomize}
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all"
            style={{ background: 'linear-gradient(135deg,#5b35e8,#ff5c2b)' }}
          >
            <Pencil size={14} />
            Customize & Use
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Template Card ─────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: EmailTemplate;
  onPreview: (t: EmailTemplate) => void;
  onCustomize: (t: EmailTemplate) => void;
}

function TemplateCard({ template, onPreview, onCustomize }: TemplateCardProps) {
  const catConfig = CATEGORIES.find((c) => c.value === template.category)!;
  const CatIcon = catConfig.icon;

  return (
    <div className="group relative bg-card border border-border rounded-xl overflow-hidden hover:shadow-md hover:border-primary/30 transition-all duration-200 flex flex-col">
      {/* Mini preview */}
      <div className="relative h-44 bg-secondary/30 overflow-hidden border-b border-border">
        <iframe
          srcDoc={template.bodyHtml}
          title={`Mini preview of ${template.name}`}
          className="w-full h-full pointer-events-none"
          style={{ transform: 'scale(0.55)', transformOrigin: 'top left', width: '182%', height: '182%' }}
          sandbox="allow-same-origin"
        />
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/10 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
          <button
            onClick={() => onPreview(template)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card/95 text-sm font-semibold text-foreground shadow-sm hover:bg-card transition-all"
          >
            <Eye size={14} />
            Preview
          </button>
          <button
            onClick={() => onCustomize(template)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-bold text-white shadow-sm transition-all"
            style={{ background: 'linear-gradient(135deg,#5b35e8,#ff5c2b)' }}
          >
            <Pencil size={14} />
            Customize
          </button>
        </div>
        {template.featured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/90 text-amber-900 text-xs font-bold">
            <Star size={10} fill="currentColor" />
            Featured
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-sm text-foreground leading-tight">{template.name}</h3>
          <div className="flex items-center gap-1 flex-shrink-0" style={{ color: catConfig.color }}>
            <CatIcon size={13} />
            <span className="text-xs font-semibold capitalize">{template.category}</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">{template.description}</p>
        <div className="flex items-center justify-between mt-auto pt-1">
          <span className="text-xs text-muted-foreground">{template.usageCount.toLocaleString()} uses</span>
          <div className="flex gap-1.5">
            <button
              onClick={() => onPreview(template)}
              className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
              title="Preview"
            >
              <Eye size={14} />
            </button>
            <button
              onClick={() => onCustomize(template)}
              className="p-1.5 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-all"
              title="Customize"
            >
              <Pencil size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Client ───────────────────────────────────────────────────────────────

export default function EmailTemplatesClient() {
  const [category, setCategory] = useState<TemplateCategory>('all');
  const [search, setSearch] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null);
  const [customizeTemplate, setCustomizeTemplate] = useState<EmailTemplate | null>(null);
  const [usedTemplates, setUsedTemplates] = useState<Set<string>>(new Set());

  const filtered = TEMPLATES.filter((t) => {
    const matchCat = category === 'all' || t.category === category;
    const q = search.toLowerCase();
    const matchSearch = !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q) || t.tags.some((tag) => tag.includes(q));
    return matchCat && matchSearch;
  });

  const handleUse = (template: EmailTemplate, _subject: string, _body: string) => {
    setUsedTemplates((prev) => new Set(prev).add(template.id));
    // Navigate to campaign management with template pre-filled (future integration point)
    window.location.href = '/campaign-management';
  };

  const handlePreviewCustomize = (template: EmailTemplate) => {
    setPreviewTemplate(null);
    setCustomizeTemplate(template);
  };

  return (
    <div className="flex flex-col gap-6 p-6 min-h-full">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight">Email Templates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Pre-built templates to launch campaigns 5× faster. Customize and reuse.</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/60 px-3 py-2 rounded-lg">
          <span className="font-semibold text-foreground">{TEMPLATES.length}</span> templates available
        </div>
      </div>

      {/* Category tabs + Search */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-1 p-1 bg-secondary rounded-xl flex-wrap">
          {CATEGORIES.map((cat) => {
            const CatIcon = cat.icon;
            const count = cat.value === 'all' ? TEMPLATES.length : TEMPLATES.filter((t) => t.category === cat.value).length;
            return (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                  category === cat.value
                    ? 'bg-card shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <CatIcon size={14} style={{ color: category === cat.value ? cat.color : undefined }} />
                {cat.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${category === cat.value ? 'bg-primary/10 text-primary' : 'bg-border text-muted-foreground'}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="pl-9 pr-4 py-2 rounded-lg border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 w-56"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Mail size={40} className="text-muted-foreground/40 mb-3" />
          <p className="font-semibold text-foreground">No templates found</p>
          <p className="text-sm text-muted-foreground mt-1">Try a different category or search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onPreview={setPreviewTemplate}
              onCustomize={setCustomizeTemplate}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          onCustomize={() => handlePreviewCustomize(previewTemplate)}
        />
      )}
      {customizeTemplate && (
        <CustomizeModal
          template={customizeTemplate}
          onClose={() => setCustomizeTemplate(null)}
          onUse={handleUse}
        />
      )}
    </div>
  );
}
